// Event Comments Section Web Component
import { escapeHtml } from '../utils/html-utils.js';
class EventComments extends HTMLElement {
  constructor() {
    super();
    this.eventId = null;
    this.comments = [];
    this.isLoading = false;
    this.error = null;
    this.replyingTo = null;
    this.currentUser = null;
    this.isPublic = false;
    this.editingCommentId = null;
    this.subscriptionStatus = null;
    // These will be set by parent component
    this.renderWatchButton = null;
    this.onWatchButtonClick = null;
    this.onWatchOptionClick = null;
  }

  static get observedAttributes() {
    return ['event-id', 'is-public'];
  }

  connectedCallback() {
    this.render();
    if (this.eventId) {
      this.loadComments();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.commentActionsListener) {
      this.removeEventListener('click', this.commentActionsListener);
      this.commentActionsListener = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'event-id' && newValue !== oldValue) {
      this.eventId = newValue;
      this.comments = [];
      this.loadComments();
    }
    if (name === 'is-public' && newValue !== oldValue) {
      this.isPublic = newValue === 'true';
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.populateAliasFields();
  }

  setSubscriptionStatus(status) {
    this.subscriptionStatus = status;
    this.render();
  }

  render() {
    this.innerHTML = `
      <div class="event-comments">
        <div class="comments-header">
          <h3>💬 Discussion</h3>
          <div class="comments-header-right">
            ${this.renderWatchButton ? this.renderWatchButton() : ''}
            <div class="comments-count" id="commentsCount">${this.comments.length > 0 ? this.comments.length : '0'}</div>
          </div>
        </div>

        ${this.isPublic ? this.renderPublicEventWarning() : ''}

        <div class="comments-content">
          ${this.renderContent()}
        </div>
      </div>
    `;

    // Re-attach event listeners after render
    this.attachEventListeners();
  }

  renderPublicEventWarning() {
    return `
      <div class="public-event-warning">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <strong>This is a public event.</strong>
          Your comment will be visible to anyone viewing this event, including search engines.
        </div>
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading comments...</p>
        </div>
      `;
    }

    if (this.error) {
      return `
        <div class="error-state">
          <span class="error-icon">😞</span>
          <h4>Failed to Load Comments</h4>
          <p>${escapeHtml(this.error)}</p>
        </div>
      `;
    }

    const commentsHtml = this.comments.length === 0
      ? `
        <div class="empty-state">
          <h4>No comments yet</h4> — <p>Be the first to share your thoughts!</p>
        </div>
      `
      : `
        <div class="comments-list">
          ${this.renderThreadedComments()}
        </div>
      `;

    return `
      ${commentsHtml}
      ${!this.replyingTo ? this.renderCommentForm() : ''}
    `;
  }

  renderCommentForm(isReply = false, replyToComment = null) {
    const replyingTo = replyToComment || this.replyingTo;

    return `
      <form class="comment-form ${isReply ? 'inline-reply-form' : ''}" id="commentForm" data-reply-to="${replyingTo ? replyingTo.id : ''}">
        ${replyingTo ? `
          <div class="comment-form-header">
            <h4 class="comment-form-title">Replying to ${escapeHtml(replyingTo.username)}</h4>
            <button type="button" class="cancel-reply-btn" id="cancelReplyBtn">Cancel</button>
          </div>
        ` : ''}
        
        ${this.currentUser ? `
          <div class="alias-field">
            <label for="aliasInput" class="alias-label">
              Post as:
              <span class="mischief-icon" title="You can change your display name for this comment! Don't worry, we still know it's you 😉">
                🦝
              </span>
            </label>
            <input 
              type="text" 
              id="aliasInput" 
              name="alias" 
              class="alias-input"
              placeholder="Your display name..."
              maxlength="50"
              value="${this.currentUser && this.currentUser.username ? escapeHtml(this.currentUser.username) : ''}"
            />
          </div>
        ` : `
          <div class="guest-fields">
            <input 
              type="text" 
              id="guestNameInput" 
              name="guestName" 
              class="guest-input"
              placeholder="Your name..."
              maxlength="100"
              required
            />
            <input 
              type="email" 
              id="guestEmailInput" 
              name="guestEmail" 
              class="guest-input"
              placeholder="Your email..."
              required
            />
          </div>
        `}
        
        <markdown-preview>
          <textarea 
            id="commentTextarea" 
            name="content" 
            placeholder="${replyingTo ? 'Write your reply...' : 'Share your thoughts...'}"
            required
          ></textarea>
        </markdown-preview>
        <div class="comment-form-actions">
          <button type="submit" class="btn btn-primary" id="submitBtn">
            <span class="btn-text">${replyingTo ? 'Post Reply' : 'Post Comment'}</span>
            <span class="btn-loading" style="display: none;">⏳ Posting...</span>
          </button>
          <button type="button" class="btn btn-secondary clear-btn" id="clearBtn">Clear</button>
        </div>
      </form>
    `;
  }

  renderThreadedComments() {
    const commentTree = this.buildCommentTree(this.comments);
    return commentTree.map(comment => this.renderCommentThread(comment, 0)).join('');
  }

  buildCommentTree(comments) {
    const commentMap = new Map();
    const rootComments = [];

    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, children: [] });
    });

    comments.forEach(comment => {
      if (comment.parentId && commentMap.has(comment.parentId)) {
        commentMap.get(comment.parentId).children.push(commentMap.get(comment.id));
      } else {
        rootComments.push(commentMap.get(comment.id));
      }
    });

    return rootComments;
  }

  renderCommentThread(comment, depth = 0) {
    const createdDate = new Date(comment.createdAt);
    const timeAgo = this.formatTimeAgo(createdDate);
    const isReply = depth > 0;
    const maxDepth = 3;

    const parentContext = isReply && comment.parentId ? this.renderParentContext(comment.parentId) : '';

    const commentHtml = `
      <div class="comment-thread" data-comment-id="${comment.id}" data-depth="${depth}">
        <div class="comment-item ${isReply ? 'comment-reply' : 'comment-root'}" style="--thread-depth: ${Math.min(depth, maxDepth)}" data-depth="${depth}">
          ${parentContext}
          <div class="comment-main">
            <a name="comment-${comment.id}"></a>
            <div class="comment-header">
              <div class="comment-author">
                <div class="comment-avatar">
                  ${comment.username ? comment.username.charAt(0).toUpperCase() : '?'}
                </div>
                <div class="comment-author-info">
                  <div class="comment-author-name">
                    <user-mention username="${comment.username}" real-username="${comment.realUsername}" user-id="${this.currentUser.id}"  class="comment-author-link"></user-mention>
                  </div>
                  <div class="comment-meta">
                    <span class="comment-time" title="${createdDate.toLocaleString()}">
                      ${timeAgo}
                    </span>
                    ${isReply ? '<span class="reply-indicator">• Reply</span>' : ''}
                  </div>
                </div>
              </div>
              <div class="comment-actions">
                ${comment.isOwner ? `
                  <button class="comment-action-btn" data-action="edit" data-comment-id="${comment.id}" title="Edit comment">
                    ✏️
                  </button>
                  <button class="comment-action-btn" data-action="delete" data-comment-id="${comment.id}" title="Delete comment">
                    🗑️
                  </button>
                ` : `
                  <button class="comment-action-btn" data-action="report" data-comment-id="${comment.id}" title="Report comment">
                    🚩
                  </button>
                `}
              </div>
            </div>
            
            ${this.editingCommentId === comment.id ? this.renderEditForm(comment) : `
              <div class="comment-content">${comment.content_html || ''}</div>
              
              <div class="comment-footer">
                ${this.currentUser || this.isPublic ? `
                  <button class="btn comment-reply-btn" data-action="reply" data-comment-id="${comment.id}">
                    💬 Reply
                  </button>
                ` : ''}
                <a href="#comment-${comment.id}" class="comment-permalink" title="Link to this comment">
                  #${comment.id}
                </a>
              </div>
            `}
          </div>
        </div>
        
        ${this.replyingTo && this.replyingTo.id === comment.id ? `
          <div class="inline-reply-container">
            ${this.renderCommentForm(true, comment)}
          </div>
        ` : ''}
        
        ${comment.children && comment.children.length > 0 ? `
          <div class="comment-replies">
            ${comment.children.map(child => this.renderCommentThread(child, depth + 1)).join('')}
          </div>
        ` : ''}
      </div>
    `;

    return commentHtml;
  }

  renderParentContext(parentId) {
    const parentComment = this.comments.find(c => c.id === parentId);
    if (!parentComment) return '';

    return `
      <div class="parent-context">
        <div class="parent-context-line"></div>
        <div class="parent-context-content">
          <span class="parent-context-author">@${escapeHtml(parentComment.username || parentComment.guestName || 'Guest')}</span>
          <span class="parent-context-text">${this.truncateText(parentComment.content, 50)}</span>
        </div>
      </div>
    `;
  }

  renderEditForm(comment) {
    return `
      <form class="edit-comment-form" id="edit-form-${comment.id}">
        <div class="edit-form-group">
          <textarea 
            id="editTextarea" 
            class="edit-comment-textarea"
            placeholder="Edit your comment..."
            maxlength="2000"
            required
          >${escapeHtml(comment.content)}</textarea>
          <div class="edit-form-actions">
            <button type="submit" id="editSubmitBtn" class="edit-submit-btn">
              💾 Save
            </button>
            <button type="button" id="editCancelBtn" class="edit-cancel-btn">
              ❌ Cancel
            </button>
          </div>
          <div class="char-count">
            <span id="editCharCount">${comment.content.length}</span>/2000 characters
          </div>
        </div>
      </form>
    `;
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return escapeHtml(text);
    return escapeHtml(text.substring(0, maxLength)) + '...';
  }

  attachEventListeners() {
    this.removeEventListeners();

    const commentForms = this.querySelectorAll('.comment-form');
    commentForms.forEach(form => {
      form.addEventListener('submit', this.handleFormSubmit);
    });

    const editForms = this.querySelectorAll('.edit-comment-form');
    editForms.forEach(form => {
      form.addEventListener('submit', this.handleEditFormSubmit);
    });

    // Clear button - clears the comment form
    const clearBtns = this.querySelectorAll('#clearBtn');
    clearBtns.forEach(btn => {
      btn.addEventListener('click', this.handleClearClick);
    });

    // Cancel buttons - handle cancel reply and edit cancel buttons
    const cancelBtns = this.querySelectorAll('#cancelReplyBtn, #editCancelBtn');
    cancelBtns.forEach(btn => {
      btn.addEventListener('click', this.handleCancelClick);
    });

    if (!this.commentActionsListener) {
      this.commentActionsListener = (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        const commentId = e.target.closest('[data-action]')?.dataset.commentId;

        switch (action) {
        case 'reply':
          this.startReply(commentId);
          break;
        case 'edit':
          this.editComment(commentId);
          break;
        case 'delete':
          this.deleteComment(commentId);
          break;
        case 'report':
          this.reportComment(commentId);
          break;
        }
      };
      this.addEventListener('click', this.commentActionsListener);
    }

    const editTextareas = this.querySelectorAll('#editTextarea');
    editTextareas.forEach(textarea => {
      const charCount = textarea.parentElement.querySelector('#editCharCount');
      if (charCount) {
        textarea.addEventListener('input', () => {
          charCount.textContent = textarea.value.length;
        });
      }
    });

    // Watch button for comment notifications
    const watchBtn = this.querySelector('#watchBtn');
    if (watchBtn && this.onWatchButtonClick) {
      watchBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onWatchButtonClick();
      });
    }

    // Watch dropdown options
    const watchOptions = this.querySelectorAll('.watch-option');
    watchOptions.forEach(option => {
      option.addEventListener('click', async (e) => {
        e.stopPropagation();
        const type = e.currentTarget.dataset.type;
        if (this.onWatchOptionClick) {
          await this.onWatchOptionClick(type);
        }
      });
    });
  }

  removeEventListeners() {
    const commentForms = this.querySelectorAll('.comment-form');
    commentForms.forEach(form => {
      form.removeEventListener('submit', this.handleFormSubmit);
    });

    const editForms = this.querySelectorAll('.edit-comment-form');
    editForms.forEach(form => {
      form.removeEventListener('submit', this.handleEditFormSubmit);
    });

    const clearBtns = this.querySelectorAll('#clearBtn');
    clearBtns.forEach(btn => {
      btn.removeEventListener('click', this.handleClearClick);
    });

    const cancelBtns = this.querySelectorAll('#cancelReplyBtn, #editCancelBtn');
    cancelBtns.forEach(btn => {
      btn.removeEventListener('click', this.handleCancelClick);
    });
  }

  handleFormSubmit = (e) => {
    e.preventDefault();
    this.handleSubmit(e.target);
  };

  handleEditFormSubmit = (e) => {
    e.preventDefault();
    const commentId = e.target.id.replace('edit-form-', '');
    this.handleEditSubmit(commentId);
  };

  handleClearClick = () => {
    this.clearForm();
  };

  handleCancelClick = (e) => {
    if (e.target.id === 'cancelReplyBtn') {
      this.cancelReply();
    } else if (e.target.id === 'editCancelBtn') {
      this.cancelEdit();
    }
  };

  populateAliasFields() {
    const aliasInputs = this.querySelectorAll('.alias-input');
    aliasInputs.forEach(input => {
      if (this.currentUser && this.currentUser.username) {
        input.value = this.currentUser.username;
      }
    });
  }

  async loadComments() {
    if (!this.eventId) return;

    this.isLoading = true;
    this.error = null;
    this.updateContent();

    try {
      const response = await window.API.request(`/events/${this.eventId}/comments`);
      this.comments = response.comments || [];
      this.updateCommentsCount();
    } catch (error) {
      window.logger.error('Failed to load event comments:', error);
      this.comments = [];
      this.error = error.message || 'Failed to load comments';
    } finally {
      this.isLoading = false;
      this.updateContent();
    }
  }

  updateContent() {
    const contentContainer = this.querySelector('.comments-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderContent();
      this.attachEventListeners();
      this.populateAliasFields();
    }
  }

  updateCommentsCount() {
    const countElement = this.querySelector('#commentsCount');
    if (countElement) {
      const count = this.comments.length;
      countElement.textContent = count === 1 ? '1 comment' : `${count} comments`;
    }
  }

  async handleSubmit(form = null) {
    if (!form) {
      form = this.querySelector('.comment-form');
    }

    const textarea = form.querySelector('#commentTextarea');
    const aliasInput = form.querySelector('#aliasInput');
    const guestNameInput = form.querySelector('#guestNameInput');
    const guestEmailInput = form.querySelector('#guestEmailInput');
    const submitBtn = form.querySelector('#submitBtn');
    const content = textarea.value.trim();

    if (!content) return;

    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    const replyToId = form.dataset.replyTo;
    const isReply = replyToId && replyToId !== '';

    try {
      const requestBody = {
        content: content
      };

      if (this.currentUser) {
        const displayName = aliasInput ? aliasInput.value.trim() : this.currentUser.username;
        requestBody.displayName = displayName;
      } else {
        if (!guestNameInput || !guestEmailInput || !guestNameInput.value.trim() || !guestEmailInput.value.trim()) {
          this.showError('Please provide your name and email to comment as a guest.');
          return;
        }
        requestBody.guestName = guestNameInput.value.trim();
        requestBody.guestEmail = guestEmailInput.value.trim();
      }

      if (isReply) {
        requestBody.parentId = parseInt(replyToId);
      }

      const response = await window.API.request(`/events/${this.eventId}/comments`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      this.showSuccess(isReply ? 'Reply posted!' : 'Comment posted!');
      this.clearForm();
      await this.loadComments();

      this.dispatchEvent(new CustomEvent('comment-added', {
        detail: { comment: response.comment },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to post comment:', error);
      this.showError(error.message || 'Failed to post comment');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  startReply(commentId) {
    if (this.editingCommentId) {
      this.showError('Please finish or cancel your edit before replying to a comment.');
      return;
    }

    if (!this.currentUser && !this.isPublic) {
      this.showError('You must be logged in to reply to comments on private events.');
      return;
    }

    const comment = this.comments.find(c => c.id === parseInt(commentId));
    if (comment) {
      this.replyingTo = comment;
      this.updateContent();
      const textarea = this.querySelector('#commentTextarea');
      textarea?.focus();
    }
  }

  cancelReply() {
    this.replyingTo = null;
    this.updateContent();
  }

  clearForm() {
    this.replyingTo = null;
    const textarea = this.querySelector('#commentTextarea');
    const aliasInput = this.querySelector('#aliasInput');
    const guestNameInput = this.querySelector('#guestNameInput');
    const guestEmailInput = this.querySelector('#guestEmailInput');
    if (textarea) textarea.value = '';
    if (aliasInput) aliasInput.value = this.currentUser ? this.currentUser.username : '';
    if (guestNameInput) guestNameInput.value = '';
    if (guestEmailInput) guestEmailInput.value = '';
    this.updateContent();
  }

  editComment(commentId) {
    const numericCommentId = parseInt(commentId, 10);

    if (this.replyingTo) {
      this.showError('Please finish or cancel your reply before editing a comment.');
      return;
    }

    const comment = this.findCommentById(numericCommentId);
    if (!comment) {
      this.showError('Comment not found.');
      return;
    }

    if (!comment.isOwner) {
      this.showError('You can only edit your own comments.');
      return;
    }

    this.editingCommentId = numericCommentId;
    this.updateContent();
  }

  findCommentById(commentId, comments = this.comments) {
    for (const comment of comments) {
      if (comment.id === commentId) {
        return comment;
      }
      if (comment.children && comment.children.length > 0) {
        const found = this.findCommentById(commentId, comment.children);
        if (found) return found;
      }
    }
    return null;
  }

  async handleEditSubmit(commentId) {
    const numericCommentId = parseInt(commentId, 10);

    const editForm = this.querySelector(`#edit-form-${numericCommentId}`);
    if (!editForm) return;

    const textarea = editForm.querySelector('#editTextarea');
    const submitBtn = editForm.querySelector('#editSubmitBtn');
    const content = textarea.value.trim();

    if (!content) {
      this.showError('Comment content cannot be empty.');
      return;
    }

    if (content.length > 2000) {
      this.showError('Comment content must be less than 2000 characters.');
      return;
    }

    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    try {
      const response = await window.API.request(`/events/${this.eventId}/comments/${numericCommentId}`, {
        method: 'PUT',
        body: JSON.stringify({ content })
      });

      this.showSuccess('Comment updated successfully!');
      this.editingCommentId = null;
      await this.loadComments();

      this.dispatchEvent(new CustomEvent('comment-updated', {
        detail: { comment: response.comment },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to update comment', error);
      this.showError(error.message || 'Failed to update comment');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  cancelEdit() {
    this.editingCommentId = null;
    this.updateContent();
  }

  async deleteComment(commentId) {
    const numericCommentId = parseInt(commentId, 10);

    if (!confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    try {
      await window.API.request(`/events/${this.eventId}/comments/${numericCommentId}`, {
        method: 'DELETE'
      });

      this.showSuccess('Comment deleted successfully!');
      await this.loadComments();

      this.dispatchEvent(new CustomEvent('comment-deleted', {
        detail: { commentId: numericCommentId },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to delete comment', error);
      this.showError(error.message || 'Failed to delete comment');
    }
  }

  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  showSuccess(message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '10000';
    successDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(successDiv);
    setTimeout(() => successDiv.remove(), 4000);
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);
    setTimeout(() => errorDiv.remove(), 4000);
  }

  async reportComment(commentId) {
    const numericCommentId = parseInt(commentId, 10);

    if (!numericCommentId || isNaN(numericCommentId)) {
      window.logger.error('Event comment not found for reporting', null, { commentId: numericCommentId });
      this.showError('Invalid comment ID.');
      return;
    }

    // Show report modal
    const reason = prompt('Why are you reporting this comment?\n\nPlease provide a brief reason (e.g., "Inappropriate content", "Spam", "Harassment", etc.):');

    if (!reason || reason.trim().length === 0) {
      return; // User cancelled or didn't provide a reason
    }

    try {
      // Report event comment using the flag-post endpoint with event_comment_id
      const response = await window.API.request('/support/flag-post', {
        method: 'POST',
        body: JSON.stringify({
          event_comment_id: numericCommentId,
          reason: reason.trim(),
          description: ''
        })
      });

      if (response && response.ticket) {
        this.showSuccess('Thank you for reporting this comment. Our team will review it shortly.');
      } else {
        this.showSuccess('Thank you for reporting this comment. Our team will review it shortly.');
      }
    } catch (error) {
      window.logger.error('Failed to report event comment:', error);
      this.showError(`Failed to report comment: ${error.message || 'Please try again later.'}`);
    }
  }
}

// Register the custom element
customElements.define('event-comments', EventComments);

